import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, ProgressBar, Modal, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import LearnerNavbar from '../components/LearnerNavbar';
import '../styles/brand.css';

const TakeAssessment = () => {
  const { assessmentId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [attemptNumber, setAttemptNumber] = useState(1);

  const loadAssessment = useCallback(async () => {
    try {
      const assessmentDoc = await getDoc(doc(db, 'assessments', assessmentId));
      if (!assessmentDoc.exists()) {
        setError('Assessment not found');
        return;
      }

      const assessmentData = assessmentDoc.data();
      setAssessment(assessmentData);
      setQuestions(assessmentData.questions || []);
      
      // Check if user has already taken this assessment
      const userAttempts = assessmentData.attempts?.[currentUser.uid] || [];
      const completedAttempts = userAttempts.filter(attempt => attempt.submitted);
      
      if (completedAttempts.length >= assessmentData.maxAttempts) {
        setError('You have reached the maximum number of attempts for this assessment');
        return;
      }
      
      setAttemptNumber(completedAttempts.length + 1);
      
      // Set timer if assessment has time limit
      if (assessmentData.duration && assessmentData.duration > 0) {
        setTimeRemaining(assessmentData.duration * 60); // Convert minutes to seconds
      }
      
      setStartTime(new Date());
      
    } catch (error) {
      devError('Error loading assessment:', error);
      setError('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, currentUser.uid]);

  useEffect(() => {
    if (currentUser && userProfile?.role === 'learner') {
      loadAssessment();
    }
  }, [currentUser, userProfile, loadAssessment]);

  // Timer countdown effect
  useEffect(() => {
    if (timeRemaining > 0 && !isSubmitted) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && !isSubmitted) {
      handleSubmitAssessment(true); // Auto-submit when time runs out
    }
  }, [timeRemaining, isSubmitted]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const calculateScore = () => {
    let correctAnswers = 0;
    
    questions.forEach(question => {
      const userAnswer = answers[question.id];
      if (!userAnswer) return;
      
      switch (question.type) {
        case 'multiple-choice':
          if (userAnswer === question.correctAnswer) {
            correctAnswers++;
          }
          break;
        case 'checkbox':
          const correctOptions = question.options.filter(opt => opt.isCorrect).map(opt => opt.id);
          const userOptions = Array.isArray(userAnswer) ? userAnswer : [];
          if (JSON.stringify(correctOptions.sort()) === JSON.stringify(userOptions.sort())) {
            correctAnswers++;
          }
          break;
        case 'true-false':
          if (userAnswer === question.correctAnswer) {
            correctAnswers++;
          }
          break;
        case 'short-answer':
          // For short answer, we'll mark as correct for now (manual grading needed)
          correctAnswers++;
          break;
        default:
          break;
      }
    });
    
    return Math.round((correctAnswers / questions.length) * 100);
  };

  const handleSubmitAssessment = async (autoSubmit = false) => {
    try {
      const endTime = new Date();
      const timeTaken = Math.round((endTime - startTime) / 1000); // Time in seconds
      const score = calculateScore();
      
      const attemptData = {
        attemptNumber,
        startedAt: startTime,
        submittedAt: endTime,
        timeTaken,
        score,
        answers,
        submitted: true,
        autoSubmitted: autoSubmit
      };

      // Update assessment document with user's attempt
      const assessmentRef = doc(db, 'assessments', assessmentId);
      await updateDoc(assessmentRef, {
        [`attempts.${currentUser.uid}.${attemptNumber - 1}`]: attemptData,
        lastUpdated: serverTimestamp()
      });

      setIsSubmitted(true);
      setShowSubmitModal(false);
      
    } catch (error) {
      devError('Error submitting assessment:', error);
      setError('Failed to submit assessment. Please try again.');
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const renderQuestion = (question) => {
    const userAnswer = answers[question.id];

    switch (question.type) {
      case 'multiple-choice':
        return (
          <div>
            {question.options.map((option, index) => (
              <Form.Check
                key={index}
                type="radio"
                id={`q${question.id}_${index}`}
                name={`question_${question.id}`}
                label={option.text}
                checked={userAnswer === option.id}
                onChange={() => handleAnswerChange(question.id, option.id)}
                className="mb-2"
              />
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div>
            {question.options.map((option, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`q${question.id}_${index}`}
                label={option.text}
                checked={Array.isArray(userAnswer) && userAnswer.includes(option.id)}
                onChange={(e) => {
                  const currentAnswers = Array.isArray(userAnswer) ? userAnswer : [];
                  if (e.target.checked) {
                    handleAnswerChange(question.id, [...currentAnswers, option.id]);
                  } else {
                    handleAnswerChange(question.id, currentAnswers.filter(id => id !== option.id));
                  }
                }}
                className="mb-2"
              />
            ))}
          </div>
        );

      case 'true-false':
        return (
          <div>
            <Form.Check
              type="radio"
              id={`q${question.id}_true`}
              name={`question_${question.id}`}
              label="True"
              checked={userAnswer === true}
              onChange={() => handleAnswerChange(question.id, true)}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id={`q${question.id}_false`}
              name={`question_${question.id}`}
              label="False"
              checked={userAnswer === false}
              onChange={() => handleAnswerChange(question.id, false)}
              className="mb-2"
            />
          </div>
        );

      case 'short-answer':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={userAnswer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Type your answer here..."
          />
        );

      default:
        return <p>Unsupported question type</p>;
    }
  };

  if (userProfile?.role !== 'learner') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>Only learners can take assessments.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading assessment...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  if (isSubmitted) {
    const score = calculateScore();
    const passed = score >= assessment.passingScore;
    
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-5">
          <Card>
            <Card.Header className="text-center">
              <h3>Assessment Completed</h3>
            </Card.Header>
            <Card.Body className="text-center">
              <div className="mb-4">
                <h1 className={passed ? 'text-success' : 'text-danger'}>
                  {score}%
                </h1>
                <Badge bg={passed ? 'success' : 'danger'} className="fs-6">
                  {passed ? 'PASSED' : 'FAILED'}
                </Badge>
              </div>
              
              <div className="mb-4">
                <p><strong>Passing Score:</strong> {assessment.passingScore}%</p>
                <p><strong>Questions Answered:</strong> {getAnsweredCount()} of {questions.length}</p>
                <p><strong>Attempt:</strong> {attemptNumber} of {assessment.maxAttempts}</p>
              </div>
              
              {!passed && attemptNumber < assessment.maxAttempts && (
                <Alert variant="info">
                  <p>You have {assessment.maxAttempts - attemptNumber} attempt(s) remaining.</p>
                  <Button variant="primary" onClick={() => window.location.reload()}>
                    Retake Assessment
                  </Button>
                </Alert>
              )}
              
              <div className="mt-4">
                <Button variant="secondary" onClick={() => navigate('/dashboard')}>
                  Return to Dashboard
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  if (!assessment || questions.length === 0) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          <h4>No Questions Available</h4>
          <p>This assessment does not have any questions yet.</p>
        </Alert>
      </Container>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <>
      <LearnerNavbar />
      <Container className="mt-4">
        {/* Assessment Header */}
        <Card className="mb-4">
          <Card.Header>
            <Row>
              <Col md={8}>
                <h4>{assessment.title}</h4>
                <p className="mb-0">{assessment.description}</p>
              </Col>
              <Col md={4} className="text-end">
                {timeRemaining !== null && (
                  <div>
                    <strong>Time Remaining:</strong>
                    <div className={`fs-4 ${timeRemaining < 300 ? 'text-danger' : 'text-primary'}`}>
                      {formatTime(timeRemaining)}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Header>
        </Card>

        {/* Progress Bar */}
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between mb-2">
              <span>Progress</span>
              <span>{currentQuestionIndex + 1} of {questions.length}</span>
            </div>
            <ProgressBar now={progress} />
            <div className="mt-2 text-muted">
              <small>Questions answered: {getAnsweredCount()} of {questions.length}</small>
            </div>
          </Card.Body>
        </Card>

        {/* Question Card */}
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5>Question {currentQuestionIndex + 1}</h5>
              <Badge bg="secondary">{currentQuestion.type}</Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="mb-4">
              <h6>{currentQuestion.question}</h6>
              {currentQuestion.points && (
                <small className="text-muted">Points: {currentQuestion.points}</small>
              )}
            </div>
            
            {renderQuestion(currentQuestion)}
          </Card.Body>
        </Card>

        {/* Navigation */}
        <Card>
          <Card.Body>
            <Row>
              <Col>
                <Button
                  variant="outline-secondary"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  Previous
                </Button>
              </Col>
              <Col className="text-center">
                <Button
                  variant="warning"
                  onClick={() => setShowSubmitModal(true)}
                >
                  Submit Assessment
                </Button>
              </Col>
              <Col className="text-end">
                <Button
                  variant="primary"
                  onClick={handleNextQuestion}
                  disabled={currentQuestionIndex === questions.length - 1}
                >
                  Next
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Submit Confirmation Modal */}
        <Modal show={showSubmitModal} onHide={() => setShowSubmitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Submit Assessment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to submit your assessment?</p>
            <div className="mb-3">
              <strong>Summary:</strong>
              <ul>
                <li>Questions answered: {getAnsweredCount()} of {questions.length}</li>
                <li>Unanswered questions: {questions.length - getAnsweredCount()}</li>
                <li>Attempt: {attemptNumber} of {assessment.maxAttempts}</li>
              </ul>
            </div>
            <Alert variant="warning">
              <strong>Warning:</strong> Once submitted, you cannot change your answers for this attempt.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubmitModal(false)}>
              Continue Working
            </Button>
            <Button variant="primary" onClick={() => handleSubmitAssessment()}>
              Submit Assessment
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default TakeAssessment;